import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Logo from "./Logo";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useAdminI18n, type AdminLang } from "@/hooks/useAdminI18n";
import { roleLabels } from "@/mocks/admin";

interface NavItem {
  labelKey: string;
  path: string;
  icon: string;
  permission: string;
}

interface NavGroup {
  labelKey?: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    items: [
      { labelKey: "nav.dashboard", path: "/admin", icon: "ri-dashboard-line", permission: "dashboard" },
    ],
  },
  {
    labelKey: "nav.content",
    items: [
      { labelKey: "nav.articles", path: "/admin/artikel", icon: "ri-article-line", permission: "articles" },
      { labelKey: "nav.categories", path: "/admin/kategorien", icon: "ri-folder-line", permission: "categories" },
      { labelKey: "nav.authors", path: "/admin/autoren", icon: "ri-user-line", permission: "authors" },
      { labelKey: "nav.media", path: "/admin/mediathek", icon: "ri-image-line", permission: "media" },
    ],
  },
  {
    labelKey: "nav.website",
    items: [
      { labelKey: "nav.ads", path: "/admin/werbung", icon: "ri-advertisement-line", permission: "ads" },
      { labelKey: "nav.homepage", path: "/admin/startseite", icon: "ri-home-line", permission: "homepage" },
      { labelKey: "nav.breaking", path: "/admin/breaking", icon: "ri-flashlight-line", permission: "breaking" },
      { labelKey: "nav.seo", path: "/admin/seo", icon: "ri-search-line", permission: "seo" },
      { labelKey: "nav.newsletter", path: "/admin/newsletter", icon: "ri-mail-line", permission: "newsletter" },
      { labelKey: "nav.pages", path: "/admin/seiten", icon: "ri-pages-line", permission: "pages" },
    ],
  },
  {
    labelKey: "nav.system",
    items: [
      { labelKey: "nav.settings", path: "/admin/einstellungen", icon: "ri-settings-3-line", permission: "settings" },
      { labelKey: "nav.users", path: "/admin/benutzer", icon: "ri-shield-user-line", permission: "users" },
      { labelKey: "nav.analytics", path: "/admin/analytik", icon: "ri-bar-chart-line", permission: "analytics" },
    ],
  },
];

const langOptions: { value: AdminLang; label: string; flag: string }[] = [
  { value: "de", label: "DE", flag: "🇩🇪" },
  { value: "tr", label: "TR", flag: "🇹🇷" },
];

function LangSwitcher() {
  const { lang, setLang } = useAdminI18n();
  const [open, setOpen] = useState(false);
  const current = langOptions.find((l) => l.value === lang) ?? langOptions[0];

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-hj-gray border border-hj-gray-border rounded-md hover:bg-hj-gray-light transition-colors cursor-pointer whitespace-nowrap"
        aria-label="Sprache wechseln"
      >
        <span>{current.flag}</span>
        <span>{current.label}</span>
        <i className={`ri-arrow-down-s-line text-xs transition-transform ${open ? "rotate-180" : ""}`}></i>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-hj-gray-border rounded-lg overflow-hidden min-w-[90px]">
            {langOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => {
                  setLang(opt.value);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors cursor-pointer ${
                  lang === opt.value
                    ? "bg-hj-red/10 text-hj-red"
                    : "text-hj-dark hover:bg-hj-gray-light"
                }`}
              >
                <span>{opt.flag}</span>
                <span>{opt.label}</span>
                {lang === opt.value && <i className="ri-check-line ml-auto text-hj-red"></i>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, loading, logout, hasPermission } = useAdminAuth();
  const { t } = useAdminI18n();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (!loading && !session) {
      navigate("/admin/login");
    }
  }, [session, loading, navigate]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [sidebarOpen]);

  const toggleGroup = (index: number) => {
    setCollapsedGroups((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-hj-gray-light flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-hj-red border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!session) {
    return null;
  }

  return (
    <div className="min-h-screen bg-hj-gray-light flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-[280px] bg-hj-dark text-white transform transition-transform duration-300 lg:translate-x-0 lg:static lg:inset-auto flex flex-col ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        aria-label="Admin Navigation"
      >
        {/* Sidebar header */}
        <div className="p-4 border-b border-white/10 shrink-0 flex items-center justify-between">
          <Logo variant="admin" />
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors cursor-pointer"
            aria-label={t("nav.closeMenu")}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Nav groups */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {navGroups.map((group, gi) => {
            const visibleItems = group.items.filter((item) => hasPermission(item.permission));
            if (visibleItems.length === 0) return null;

            const isCollapsed = collapsedGroups[gi] ?? false;
            const hasLabel = !!group.labelKey;

            return (
              <div key={gi} className="mb-1">
                {hasLabel ? (
                  <button
                    onClick={() => toggleGroup(gi)}
                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-md text-[11px] font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-400 transition-colors cursor-pointer"
                    aria-expanded={!isCollapsed}
                  >
                    <span>{t(group.labelKey!)}</span>
                    <i className={`ri-arrow-down-s-line text-sm transition-transform ${isCollapsed ? "-rotate-90" : ""}`}></i>
                  </button>
                ) : null}

                <div className={`space-y-0.5 ${hasLabel && isCollapsed ? "hidden" : "block"}`}>
                  {visibleItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center gap-3 px-3 py-3 rounded-md text-sm transition-colors min-h-[44px] ${
                          isActive
                            ? "bg-hj-red text-white"
                            : "text-gray-300 hover:bg-white/10 hover:text-white"
                        }`}
                        aria-current={isActive ? "page" : undefined}
                      >
                        <i className={`${item.icon} text-lg w-5 text-center shrink-0`}></i>
                        <span className="truncate">{t(item.labelKey)}</span>
                        {isActive && (
                          <i className="ri-arrow-right-s-line ml-auto text-sm shrink-0"></i>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Sidebar footer */}
        <div className="shrink-0 border-t border-white/10">
          <div className="px-4 py-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">{t("nav.loggedInAs")}</p>
            <p className="text-sm font-medium text-white truncate">{session.name || session.email}</p>
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mt-0.5">
              {roleLabels[session.role]}
            </p>
          </div>
          <div className="px-3 pb-2">
            <button
              onClick={logout}
              className="flex items-center gap-3 px-3 py-3 rounded-md text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-colors w-full min-h-[44px] cursor-pointer"
            >
              <i className="ri-logout-box-line text-lg w-5 text-center shrink-0"></i>
              {t("nav.logout")}
            </button>
          </div>
          {/* KreativWeb.ch credit */}
          <div className="px-4 py-3 border-t border-white/10">
            <p className="text-[10px] text-gray-600 text-center">
              {t("footer.developedBy")}{" "}
              <a
                href="https://kreativweb.ch"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-gray-300 transition-colors"
              >
                KreativWeb.ch
              </a>
            </p>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <div className="bg-white border-b border-hj-gray-border px-4 md:px-6 py-3 flex items-center justify-between shrink-0 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
              aria-label={t("nav.openMenu")}
            >
              <i className="ri-menu-line text-xl text-hj-dark"></i>
            </button>
            <span className="text-sm text-hj-gray hidden sm:inline font-body">{t("nav.adminPanel")}</span>
          </div>
          <div className="flex items-center gap-2">
            <LangSwitcher />
            <Link
              to="/"
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-hj-gray border border-hj-gray-border rounded-md hover:bg-hj-gray-light transition-colors whitespace-nowrap"
            >
              <i className="ri-external-link-line"></i>
              {t("nav.viewWebsite")}
            </Link>
            <div className="w-9 h-9 rounded-full bg-hj-red/10 flex items-center justify-center">
              <i className="ri-user-line text-hj-red"></i>
            </div>
          </div>
        </div>

        {/* Page content */}
        <div className="flex-1 p-4 md:p-6 overflow-y-auto">{children}</div>

        {/* Bottom footer with KreativWeb.ch */}
        <div className="shrink-0 bg-white border-t border-hj-gray-border px-4 md:px-6 py-2.5 flex items-center justify-between">
          <span className="text-[11px] text-gray-400 font-body">
            {t("footer.adminPanel")} &copy; {new Date().getFullYear()}
          </span>
          <span className="text-[11px] text-gray-400">
            {t("footer.developedBy")}{" "}
            <a
              href="https://kreativweb.ch"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-hj-red transition-colors font-medium"
            >
              KreativWeb.ch
            </a>
          </span>
        </div>
      </div>
    </div>
  );
}
